import React, { useState, useImperativeHandle, useMemo } from 'react';
import {
    Text,
    View,
    TouchableOpacity,
    TextInput,
    FlatList, ScrollView,
} from 'react-native-web';
import PropTypes from 'prop-types';
import Icon from 'react-native-vector-icons/MaterialIcons';
import * as Animatable from 'react-native-animatable';

// Custom Components
import { NavBar } from './component_nav_bar';
import { Button } from './component_button';
import { CustomModal } from './component_custom_modal';

// Configs
import GLOBAL from '../configs/config_global';

// Utils
import FeatherIcon from 'react-native-vector-icons/dist/Feather';

// Animatable
const AnimatableFlatList = Animatable.createAnimatableComponent(FlatList);

export function CuisinePickerModal(props, ref) {
    const { onSelectComplete, selectedCuisines } = props;

    // region Local State
    const [modalShown, setModalShown] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [compShouldLoad, setCompShouldLoad] = useState(false);
    const [localSelectedCuisines, setLocalSelectedCuisines] = useState([]);
    // endregion

    // region Function - Show/Hide Modal
    const showModal = () => {
        setModalShown(true);
        setLocalSelectedCuisines(selectedCuisines);
        setTimeout(() => setCompShouldLoad(true), 500);
    }

    const hideModal = () => {
        setModalShown(false);
        setCompShouldLoad(false);
        setSearchText('');
        setLocalSelectedCuisines([]);
    }
    // endregion

    // region Expose Methods
    useImperativeHandle(ref, () => ({ showModal, hideModal }), [selectedCuisines]);
    // endregion

    // region Render Cuisine Item
    const renderCuisineItem = (item) => {
        const isChosen = localSelectedCuisines.find((cuisine) => cuisine.id === item.id);
        return(
            <TouchableOpacity
                key={`cuisineItem_${item.id}`}
                onPress={() => {
                    if (isChosen) {
                        setLocalSelectedCuisines(localSelectedCuisines.filter((cuisine) => cuisine.id !== item.id));
                    }
                    else {
                        if (localSelectedCuisines.length < 5) {
                            setLocalSelectedCuisines(localSelectedCuisines.concat(item));
                        }
                    }
                }}
                style={{ width: '100%', flexDirection: 'row', alignItems: 'center', paddingVertical: 14, borderBottomWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, backgroundColor: 'white', paddingHorizontal: 24 }}>
                <Text style={{ color: 'black', fontSize: 18, flex: 1 }}>
                    {item.name?.trim() || '[UNKNOWN CUISINE]'}
                </Text>

                <View style={{
                    borderWidth: 2, borderRadius: 4, borderColor: isChosen ? GLOBAL.COLOR.MAIN : GLOBAL.COLOR.TEXT_GRAY,
                    backgroundColor: isChosen ? GLOBAL.COLOR.MAIN : 'white', height: 24, width: 24,
                    alignItems: 'center', justifyContent: 'center'
                }}>
                    <Icon name={'check'} size={16} color={'white'} />
                </View>
            </TouchableOpacity>
        );
    }
    // endregion

    // region Render Search Bar
    const renderSearchBar = () => (
        <View style={{
            alignSelf: 'stretch', alignItems: 'center', flexDirection: 'row',
            backgroundColor: GLOBAL.COLOR.BASIC.WHITE_TWO,
            borderRadius: 5, borderColor: 'rgba(0, 0, 0, 0.08)', marginTop: 24, padding: 12
        }}>
            {/* Search Icon */}
            <FeatherIcon
                name={'search'}
                size={18}
                color={searchText ? GLOBAL.COLOR.TEXT_GRAY : GLOBAL.COLOR.BASIC.PINKISH_GREY}
            />

            {/* Search Text Input */}
            <TextInput
                autoCorrect={false}
                onChangeText={setSearchText}
                value={searchText}
                blurOnSubmit={true}
                title={'Find Cuisine'}
                placeholder={'Find cuisine'}
                placeholderTextColor={GLOBAL.COLOR.BASIC.PINKISH_GREY}
                style={{ fontSize: 16, color: GLOBAL.COLOR.BASIC.WARM_GREY, marginLeft: 12, flex: 1, outlineStyle: 'none' }}
            />

            {/* Clear Text Button */}
            {
                searchText.trim() ?
                    <TouchableOpacity
                        style={{ marginRight: 2 }}
                        onPress={() => setSearchText('')}>
                        <Icon
                            name={'close'}
                            size={20}
                            color={GLOBAL.COLOR.BASIC.PINKISH_GREY}
                        />
                    </TouchableOpacity>
                    :
                    false
            }
        </View>
    )
    // endregion

    // region MAIN RENDER
    const cuisineData = useMemo(() => GLOBAL.CUISINE_LIST.filter((item) => !searchText?.trim() ? true : item.name.toLowerCase().toLowerCase().includes(searchText.toLowerCase())), [GLOBAL.CUISINE_LIST, searchText]);
    return(
        <CustomModal visible={modalShown}>
            {/* Nav Bar */}
            <NavBar
                leftButtonOnPress={hideModal}
                title={'Cuisine Selector'}
            />

            {/* Main View */}
            <ScrollView style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }} contentContainerStyle={{ paddingBottom: 128 }}>
                {/* Instruction */}
                <View style={{ alignItems: 'center', alignSelf: 'stretch', padding: 24, borderBottomWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, backgroundColor: 'white' }}>
                    <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, fontSize: 15, textAlign: 'center' }}>
                        You can select up to 5 cuisine types for your truck.
                    </Text>

                    <Text style={{ fontWeight: 'bold', color: GLOBAL.COLOR.TEXT_GRAY, fontSize: 15, textAlign: 'center', marginTop: 4 }}>
                        Tip:{' '}
                        <Text style={{ fontWeight: 'normal', color: GLOBAL.COLOR.TEXT_GRAY, fontSize: 15, textAlign: 'center' }}>
                            If your truck serves a lot of different cuisines, consider using "Multi-Cuisine".
                        </Text>
                    </Text>

                    {/* Search Bar */}
                    {renderSearchBar()}

                    {/* Selected Cuisine List */}
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignSelf: 'stretch', marginTop: 8 }}>
                        {
                            localSelectedCuisines.map((item) => (
                                <TouchableOpacity
                                    key={`selectedCuisineItem_${item.id}`}
                                    onPress={() => setLocalSelectedCuisines(localSelectedCuisines.filter((cuisine) => cuisine.id !== item.id))}
                                    style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, paddingLeft: 12, paddingRight: 10, borderRadius: 16, backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY, marginRight: 6, marginTop: 6 }}>
                                    <Text style={{ fontWeight: '500', fontSize: 16, marginRight: 2 }}>
                                        {item.name}
                                    </Text>

                                    <Icon
                                        name={'close'}
                                        color={'black'}
                                        size={16}
                                    />
                                </TouchableOpacity>
                            ))
                        }
                    </View>
                </View>

                {/* Content */}
                <View style={{ width: '100%', flex: 1, backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}>
                    {compShouldLoad && cuisineData.map((item) => renderCuisineItem(item))}
                </View>
            </ScrollView>

            {/* Apply Button */}
            <View
                style={{
                    width: '100%', backgroundColor: 'white',
                    alignItems: 'center', paddingHorizontal: 24, paddingBottom: 24, paddingTop: 16,
                    borderTopWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER
                }}>
                <Button
                    onPress={() => {
                        onSelectComplete(localSelectedCuisines);
                        hideModal();
                    }}
                    text={'Done'}
                    textStyle={{ color: 'white' }}
                    backgroundColor={GLOBAL.COLOR.MAIN}
                    locked={localSelectedCuisines.length === 0}
                />
            </View>
        </CustomModal>
    );
    // endregion
}

// Forward Ref
CuisinePickerModal = React.forwardRef(CuisinePickerModal);

// region Props
CuisinePickerModal.propTypes = {
    onSelectComplete: PropTypes.func,
    localSelectedCuisines: PropTypes.array
};

CuisinePickerModal.defaultProps = {
    onSelectComplete: () => {},
    localSelectedCuisines: []
};
// endregion
